import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getWorkOrders, getSchedule, getTimeEntries } from '../../services/api';
import { Link } from 'react-router-dom';
import { ClipboardList, Clock, Calendar, MessageSquare, ArrowRight } from 'lucide-react';
import { StatCard, PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { todayIso } from '../../lib/dateUtils';

const EmployeeOverview = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [entries, setEntries] = useState([]);
  useEffect(() => { Promise.all([getWorkOrders(), getSchedule(), getTimeEntries()]).then(([w, s, t]) => { setOrders(w); setShifts(s); setEntries(t); }); }, []);

  const myOrders = orders.filter(w => w.assigneeId === user.id || (w.assigneeIds || []).includes(user.id));

  const active = myOrders.filter(w => w.status !== 'completed');
  const today = todayIso();
  const todayShift = shifts.find(s => s.date === today) || shifts[0];
  const weekHours = entries.reduce((s, t) => s + t.hours, 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader title={`Hi ${user.name.split(' ')[0]}`} description="Your dashboard for today's work." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ClipboardList} label="Active Orders" value={active.length} hint={`${myOrders.length} total assigned`} accent="blue" to="/employee/work-orders" />
        <StatCard icon={Clock} label="Hours This Week" value={weekHours.toFixed(1)} hint="Across all jobs" accent="amber" to="/employee/time-clock" />
        <StatCard icon={Calendar} label="Shifts This Week" value={shifts.length} hint={todayShift ? `Next: ${todayShift.date}` : 'No shifts'} accent="slate" to="/employee/schedule" />
        <StatCard icon={MessageSquare} label="Team Chat" value={'Open'} hint="Channels & DMs" accent="green" to="/employee/chat" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <h2 className="font-display uppercase tracking-wide">Your Active Work Orders</h2>
            <Link to="/employee/work-orders" className="text-xs font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="divide-y divide-slate-100">
            {active.length === 0 && <div className="p-8 text-center text-slate-500">No active work orders. Great job!</div>}
            {active.map(w => (
              <div key={w.id} className="p-5 hover:bg-slate-50 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-slate-500">{w.id}</span>
                    <PriorityBadge priority={w.priority} />
                  </div>
                  <div className="font-medium text-slate-900">{w.title}</div>
                  <div className="text-xs text-slate-500 mt-1">Due {w.dueDate} · {w.asset}</div>
                </div>
                <StatusBadge status={w.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-900 text-white rounded-md p-6">
          <Clock className="h-8 w-8 text-blue-700 mb-3" />
          <h2 className="font-display uppercase mb-2">Time Clock</h2>
          <p className="text-sm text-slate-300 mb-4">Clock in or out from the time tracker.</p>
          <Link to="/employee/time-clock"><button className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold h-11 rounded">Open Time Clock</button></Link>
          {todayShift && (
            <div className="mt-6 pt-6 border-t border-slate-700">
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Next shift</div>
              <div className="text-sm font-semibold">{todayShift.date}</div>
              <div className="text-xs text-slate-300">{todayShift.shiftStart} - {todayShift.shiftEnd} · {todayShift.site}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeOverview;
